import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRegistrationDiagnostics = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [diagnosis, setDiagnosis] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [telemetryData, setTelemetryData] = useState({
    page: 'signup',
    events: [],
    ab_flags: { captcha: 'on', magiclink: 'off' }
  });
  const [feedback, setFeedback] = useState([]);

  const runDiagnosis = async () => {
    setLoading(true);
    try {
      // Get diagnosis
      const diagResponse = await axios.post(
        `${API}/orchestrator/registration/diagnose`,
        {
          telemetry: telemetryData,
          last_100_feedback: feedback
        },
        { withCredentials: true }
      );
      
      setDiagnosis(diagResponse.data.diagnosis);
      
      // Get metrics
      const metricsResponse = await axios.post(
        `${API}/orchestrator/registration/metrics`,
        telemetryData,
        { withCredentials: true }
      );
      
      setMetrics(metricsResponse.data.metrics);
      
    } catch (error) {
      console.error('Diagnosis error:', error);
      alert('Diagnosis failed');
    } finally {
      setLoading(false);
    }
  };

  const addSampleEvent = (type) => {
    const event = {
      t: new Date().toISOString(),
      type: type,
      payload: {},
      http_status: type === 'error' ? 500 : 200,
      err: type === 'error' ? 'db_conn_timeout' : null
    };
    
    setTelemetryData(prev => ({
      ...prev,
      events: [...prev.events, event]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar title={language === 'tr' ? 'Kayıt Tanılama' : 'Registration Diagnostics'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <h1 className="text-4xl font-bold mb-2">
            🔬 {language === 'tr' ? 'Kayıt Akışı Tanılama' : 'Registration Flow Diagnostics'}
          </h1>
          <p className="text-purple-100 text-lg">
            {language === 'tr' 
              ? 'Kayıt sorunlarını tespit et ve çözümler öner'
              : 'Diagnose registration issues and suggest fixes'}
          </p>
        </div>

        {/* Telemetry Input */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">📊 Telemetry Data</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block font-semibold mb-2">Events ({telemetryData.events.length})</label>
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => addSampleEvent('submit')}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  Add Submit Event
                </button>
                <button
                  onClick={() => addSampleEvent('error')}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                >
                  Add Error Event
                </button>
                <button
                  onClick={() => setTelemetryData(prev => ({ ...prev, events: [] }))}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                  Clear
                </button>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto">
                <pre className="text-xs">{JSON.stringify(telemetryData.events, null, 2)}</pre>
              </div>
            </div>

            <div>
              <label className="block font-semibold mb-2">User Feedback</label>
              <textarea
                value={feedback.join('\n')}
                onChange={(e) => setFeedback(e.target.value.split('\n').filter(f => f.trim()))}
                placeholder="Enter user feedback (one per line)"
                rows={4}
                className="w-full p-3 border rounded-lg"
              />
            </div>
          </div>

          <button
            onClick={runDiagnosis}
            disabled={loading}
            className="mt-4 w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all disabled:opacity-50"
          >
            {loading ? (
              <span>Running Diagnosis...</span>
            ) : (
              <span>🔬 Run Diagnosis</span>
            )}
          </button>
        </div>

        {/* Diagnosis Results */}
        {diagnosis && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">🩺 Diagnosis Results</h2>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">
                  {diagnosis.health_status === 'healthy' ? '✅' :
                   diagnosis.health_status === 'critical' ? '🔴' :
                   diagnosis.health_status === 'degraded' ? '🟡' : '⚠️'}
                </span>
                <span className="text-xl font-bold capitalize">{diagnosis.health_status}</span>
              </div>

              {diagnosis.diagnosis && diagnosis.diagnosis.length > 0 && (
                <div>
                  <h3 className="font-bold text-lg mb-2">Issues Found:</h3>
                  {diagnosis.diagnosis.map((issue, idx) => (
                    <div key={idx} className="bg-red-50 border-l-4 border-red-500 p-4 mb-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{issue.pattern}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          issue.severity === 'critical' ? 'bg-red-200 text-red-800' :
                          issue.severity === 'high' ? 'bg-orange-200 text-orange-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {issue.severity}
                        </span>
                      </div>
                      <p className="text-sm mt-1">{issue.description}</p>
                      <p className="text-xs text-gray-600 mt-1">Evidence: {issue.evidence_count} occurrences</p>
                    </div>
                  ))}
                </div>
              )}

              {diagnosis.action_plan && diagnosis.action_plan.length > 0 && (
                <div>
                  <h3 className="font-bold text-lg mb-2">Action Plan:</h3>
                  {diagnosis.action_plan.map((action, idx) => (
                    <div key={idx} className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-2">
                      <div className="font-semibold">{action.fix}</div>
                      <p className="text-sm text-gray-700 mt-1">Why: {action.why}</p>
                      <p className="text-sm text-gray-700 mt-1">Owner: {action.owner}</p>
                      {action.implementation && (
                        <p className="text-xs text-gray-600 mt-2 bg-white p-2 rounded">
                          {action.implementation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {diagnosis.ux_copy_suggestions && (
                <div>
                  <h3 className="font-bold text-lg mb-2">UX Copy Suggestions:</h3>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Turkish:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {diagnosis.ux_copy_suggestions.troubleshooting_TR?.map((tip, idx) => (
                        <li key={idx} className="text-sm">{tip}</li>
                      ))}
                    </ul>
                    
                    <h4 className="font-semibold mt-4 mb-2">English:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {diagnosis.ux_copy_suggestions.troubleshooting_EN?.map((tip, idx) => (
                        <li key={idx} className="text-sm">{tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Metrics */}
        {metrics && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">📈 Metrics</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">
                  {(metrics.registration_success_rate * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Success Rate</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-3xl font-bold text-green-600">
                  {metrics.successful_registrations}
                </div>
                <div className="text-sm text-gray-600">Successful</div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-3xl font-bold text-red-600">
                  {metrics.failed_registrations}
                </div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">
                  {metrics.total_attempts}
                </div>
                <div className="text-sm text-gray-600">Total Attempts</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRegistrationDiagnostics;
